import { useNavigate } from 'react-router-dom'
import { Plus, Send } from 'lucide-react'
import type { OutreachListItem } from '@/api/client'
import { fmtCount, repliedOf, sentOf, useOutreaches } from '@/lib/creators'
import { Button, ButtonLink, DataTable, EmptyState, PageHeader, PlatformMark, StatusPill, useWidePage, type Column } from '@/ui'
import { CreatorsTabs } from './CreatorsTabs'

const columns: Column<OutreachListItem>[] = [
  {
    key: 'name',
    header: '이름',
    card: 'title',
    className: 'max-w-[18rem]',
    cell: (o) => (
      <span className="flex min-w-0 items-center gap-1.5">
        <PlatformMark platform={o.platform} />
        <span className="truncate font-medium">{o.name}</span>
      </span>
    ),
  },
  { key: 'status', header: '상태', card: 'meta', cell: (o) => <StatusPill domain="outreach" status={o.status} /> },
  { key: 'pool', header: '풀', cell: (o) => <span className="block truncate">{o.pool_name}</span>, className: 'max-w-[14rem]' },
  {
    key: 'ready',
    header: '승인 대기',
    align: 'right',
    cell: (o) => <span className={o.counts.ready > 0 ? 'font-semibold tabular-nums text-status-review' : 'tabular-nums'}>{fmtCount(o.counts.ready)}</span>,
  },
  { key: 'sent', header: '보냄', align: 'right', cell: (o) => <span className="tabular-nums">{fmtCount(sentOf(o.counts))}</span> },
  { key: 'replies', header: '답장', align: 'right', cell: (o) => <span className="tabular-nums">{fmtCount(repliedOf(o.counts))}</span> },
  {
    key: 'today',
    header: '오늘',
    align: 'right',
    cell: (o) => (
      <span className="tabular-nums">
        {o.sent_today} / {o.daily_limit}
      </span>
    ),
  },
]

/** /creators/outreaches: every DM outreach made from a pool. */
export default function OutreachList() {
  useWidePage()
  const q = useOutreaches()
  const navigate = useNavigate()
  const items = q.data?.items ?? []
  const newLink = (
    <ButtonLink variant="primary" icon={Plus} to="/creators/outreaches/new">
      새 발송
    </ButtonLink>
  )
  return (
    <>
      <PageHeader
        title="DM 보내기"
        docTitle="인플루언서 · DM 보내기"
        description="풀에서 사람을 골라 그룹으로 나누고, 그룹마다 다른 메시지를 한 명씩 승인해 보낸다."
        actions={items.length > 0 ? newLink : undefined}
      >
        <CreatorsTabs />
      </PageHeader>
      <DataTable
        label="발송 목록"
        columns={columns}
        rows={q.data ? items : undefined}
        rowKey={(o) => o.id}
        onRowClick={(o) => navigate(`/creators/outreaches/${o.id}`)}
        loading={q.isPending}
        error={q.error && !q.data ? q.error : undefined}
        onRetry={() => q.refetch()}
        empty={
          <EmptyState
            icon={Send}
            title="발송이 없다"
            description="타깃 풀에서 연락할 사람을 고르고, 나누고, 메시지를 정하면 발송이 만들어진다."
            actions={<Button variant="primary" icon={Plus} onClick={() => navigate('/creators/outreaches/new')}>새 발송</Button>}
          />
        }
      />
    </>
  )
}
